import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * The RefObject is typed as any because it needs to account for any possible
 * HTML Element
 */
export const useIsElementAtTopOfViewWithIntersectionObserver = (
  parentElement?: HTMLElement
): [RefObject<any>, boolean] => {
  const elementRef = useRef<HTMLElement | null>(null);
  const [isElementAtTopOfView, setIsElementAtTopOfView] = useState(false);

  useEffect(() => {
    function isScrolledCallback(entries) {
      if (entries) {
        setIsElementAtTopOfView(entries[0]?.intersectionRatio < 1);
      }
    }

    const observer = new IntersectionObserver(isScrolledCallback, {
      root: parentElement,
      rootMargin: '-1px 0px 100% 0px',
      threshold: [0.9, 1.0],
    });

    if (elementRef.current) {
      observer.observe(elementRef.current as HTMLElement);
    }

    return () => {
      observer?.disconnect();
    };
  }, [parentElement]);

  return [elementRef, isElementAtTopOfView];
};

/**
 * The RefObject is typed as any because it needs to account for any possible
 * HTML Element
 */
export const useIsElementAtTopOfViewWithScrollEvent = (
  parentElement?: HTMLElement | null
): [RefObject<any>, boolean] => {
  const elementRef = useRef<HTMLElement | null>(null);
  const [isElementAtTopOfView, setIsElementAtTopOfView] = useState(false);

  useEffect(() => {
    function handleScroll(event) {
      setIsElementAtTopOfView(
        event.target &&
          elementRef?.current?.offsetTop <= event.target.scrollTop &&
          event.target.scrollTop > 0
      );
    }

    function onScroll(event) {
      requestAnimationFrame(() => handleScroll(event));
    }

    if (parentElement) {
      parentElement.addEventListener('scroll', onScroll);
    }

    return () => parentElement?.removeEventListener('scroll', onScroll);
  }, [elementRef, parentElement]);

  return [elementRef, isElementAtTopOfView];
};
